import React, { FC, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import type { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import { fmtInt, fmtWeekStart } from './format';

/**
 * Week-over-week stacked bar used twice on the tab: once stacked by task kind
 * (all human web work) and once stacked by blocker (the blocked subset).
 * The current week is the last bucket, drawn hatched and labelled "so far".
 * Segments are ordered by overall frequency so the largest sits at the
 * bottom; the legend lets a reader hide/show a segment.
 */

export interface StackSeries {
  key: string;
  label: string;
  colour: string;
  values: number[];
}

export interface StackBucket {
  weekStart: string;
  isCurrent: boolean;
}

export interface KindStackChartProps {
  buckets: readonly StackBucket[];
  series: StackSeries[];
  /** Test id on the wrapper so tests can find the right chart. */
  testId?: string;
  ariaLabel: string;
}

const patternCache = new Map<string, CanvasPattern | string>();

/** Diagonal hatch in `colour`; falls back to the flat colour without a 2D canvas (jsdom). */
export function hatchPattern(colour: string): CanvasPattern | string {
  const cached = patternCache.get(colour);
  if (cached) return cached;
  const tile = document.createElement('canvas');
  tile.width = 8;
  tile.height = 8;
  const ctx = tile.getContext('2d');
  if (!ctx) return colour;
  ctx.fillStyle = colour;
  ctx.globalAlpha = 0.35;
  ctx.fillRect(0, 0, 8, 8);
  ctx.globalAlpha = 1;
  ctx.strokeStyle = colour;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, 8);
  ctx.lineTo(8, 0);
  ctx.moveTo(-2, 2);
  ctx.lineTo(2, -2);
  ctx.moveTo(6, 10);
  ctx.lineTo(10, 6);
  ctx.stroke();
  const pattern = ctx.createPattern(tile, 'repeat') ?? colour;
  patternCache.set(colour, pattern);
  return pattern;
}

export const xLabel = (b: StackBucket): string =>
  b.isCurrent
    ? `${fmtWeekStart(b.weekStart)} (so far)`
    : fmtWeekStart(b.weekStart);

export const KindStackChart: FC<KindStackChartProps> = ({
  buckets,
  series,
  testId = 'stack-chart',
  ariaLabel,
}) => {
  const data = useMemo<ChartData<'bar'>>(
    () => ({
      labels: buckets.map(xLabel),
      datasets: series.map((s) => ({
        label: s.label,
        data: s.values.map((v) => (Number.isFinite(v) ? v : 0)),
        backgroundColor: buckets.map((b) =>
          b.isCurrent ? hatchPattern(s.colour) : s.colour,
        ),
        borderColor: s.colour,
        borderWidth: buckets.map((b) => (b.isCurrent ? 1 : 0)),
        borderSkipped: false,
        maxBarThickness: 56,
      })),
    }),
    [buckets, series],
  );

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 } },
        },
        tooltip: {
          callbacks: {
            label: (item: TooltipItem<'bar'>) =>
              ` ${item.dataset.label}: ${fmtInt(item.parsed.y)}`,
            footer: (items: TooltipItem<'bar'>[]) =>
              `Total: ${fmtInt(
                items.reduce((sum, i) => sum + (i.parsed.y ?? 0), 0),
              )}`,
          },
        },
      },
      scales: {
        x: { stacked: true, grid: { display: false } },
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: { precision: 0 },
          grid: { color: 'rgba(148, 163, 184, 0.15)' },
        },
      },
    }),
    [],
  );

  return (
    <div
      className='scaling-chart scaling-chart--tall wwm-chart'
      data-testid={testId}
      role='img'
      aria-label={ariaLabel}
    >
      <Bar data={data} options={options} />
    </div>
  );
};
